/**
 * Deal Cycle dashboard — "where do my deals sit right now?"
 *
 * Surfaces:
 *   - 4-stage funnel (Qualifying Calls → Demos → Decision Rooms →
 *     Closed Won) with drop-off % between adjacent stages and a trend
 *     delta vs the previous window
 *   - Stuck deals (open Decision Rooms with last_activity_at >7 days),
 *     highest deal value first so the costliest silence tops the list
 *   - Hot this week (Decision Rooms with most stakeholder views in
 *     the last 7 days)
 *   - Forecast (open Decision Rooms × deal_value_cents)
 *
 * Fetches GET /api/dashboard/deal-cycle?period=...&userId=... and
 * renders the result. Loading/error/empty states all handled inline.
 *
 * Styling: shared .dash-card system in App.css (2026-07-26 design
 * audit item 5) — component-specific layout stays inline, card
 * chrome / lists / hovers come from the shared classes.
 */

import { useEffect, useState } from 'react';
import DeltaChip from './DeltaChip';
import { fmtMoney, pct, periodLabel } from '../utils/dashFormat';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

// Pipeline-health table cells (mirrors TeamPerformanceDashboard's
// table styling so the two admin tables read as one system).
const hlTh = {
  padding: '10px 12px', textAlign: 'left',
  fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};
const hlThRight = { ...hlTh, textAlign: 'right' };
const hlTd = { padding: '12px', verticalAlign: 'middle' };
const hlTdRight = { ...hlTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--room-text-body)' };

export default function DealCycleDashboard({ authToken, period, userId, onOpenSpace, onNavigate, authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Pipeline health (roadmap #1, 2026-07-26) — independent fetch so a
  // failure here never blanks the funnel below, and vice versa.
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState(null);
  // Bumped by the Retry button; re-runs the fetch effect (Tier-1).
  const [retryNonce, setRetryNonce] = useState(0);
  // Prefer the app's authFetch (central 401 session-expiry handling);
  // raw fetch remains the fallback for any legacy call site.
  const doFetch = (url, opts) => (authFetch
    ? authFetch(url, opts)
    : fetch(url, { ...opts, headers: { ...(opts && opts.headers), Authorization: `Bearer ${authToken}` } }));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (period) params.set('period', period);
        if (userId) params.set('userId', String(userId));
        const r = await doFetch(`${API_BASE}/api/dashboard/deal-cycle?${params}`);
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    // Health list is period-independent (its windows are fixed 7/30/90d
    // signal horizons) but user-scoped, so it refetches on userId only.
    async function loadHealth() {
      setHealthError(null);
      try {
        const hp = new URLSearchParams();
        if (userId) hp.set('userId', String(userId));
        const r = await doFetch(`${API_BASE}/api/dashboard/pipeline-health?${hp}`);
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        const json = await r.json();
        if (!cancelled) setHealth(json);
      } catch (err) {
        if (!cancelled) setHealthError(err.message);
      }
    }
    loadHealth();
    return () => { cancelled = true; };
  }, [authToken, period, userId, retryNonce]);

  // Show a tiny inline loader on first load only — subsequent reloads
  // (period change, user filter change) keep the existing UI mounted.
  if (loading && !data) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--room-text-muted)' }}>
        Loading deal cycle…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{
        padding: 16, borderRadius: 12,
        background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)',
        color: '#dc2626', fontSize: 14, textAlign: 'center',
      }}>
        Couldn&apos;t load the deal-cycle data: {error}
        <div style={{ marginTop: 10 }}>
          <button type='button' onClick={() => setRetryNonce((n) => n + 1)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'var(--room-card, #fff)', color: '#b91c1c', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { funnel, stuckDeals, hotThisWeek, forecast } = data;

  // Drop-off % at each stage transition. Capped at the previous stage's
  // count so the bar widths read sanely.
  const stages = [
    { key: 'qualifyingCalls', label: 'Qualifying calls', count: funnel.qualifyingCalls,
      bar: 'linear-gradient(90deg, #6366f1, #8b5cf6)', target: 'qualifying-calls' },
    { key: 'demos',           label: 'Demos',            count: funnel.demos,
      bar: 'linear-gradient(90deg, #7c3aed, #a855f7)', target: 'library' },
    { key: 'decisionRooms',   label: 'Decision Rooms',   count: funnel.decisionRooms,
      bar: 'linear-gradient(90deg, #9333ea, #c084fc)', target: 'buyer-spaces' },
    { key: 'closedWon',       label: 'Closed won',       count: funnel.closedWon,
      bar: 'linear-gradient(90deg, #059669, #10b981)', target: 'buyer-spaces' },
  ];
  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  // Each row drills into its canonical phase: Qualifying calls → the
  // qualifying-calls phase, Demos → Library, Decision Rooms / Closed
  // Won → buyer-spaces phase. Click handler is no-op when the parent
  // didn't pass onNavigate, so the dashboard still renders for any
  // future caller that doesn't wire navigation.
  const navigateTo = (target) => {
    if (typeof onNavigate === 'function') onNavigate(target);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ─── Pipeline health (roadmap #1) ────────────────────────────
          The 10-second answer: every open room, health-ranked, worst
          first, each row pointing at an action. Rule-based score —
          weights documented in routes/dashboard.js pipeline-health. */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h3 className="dash-card-title">Pipeline health</h3>
          <span className="dash-card-sub">
            {health ? `${health.deals.length} open room${health.deals.length === 1 ? '' : 's'} · at-risk first` : 'Loading…'}
          </span>
        </div>
        {healthError ? (
          <div className="dash-empty">
            Couldn&apos;t load pipeline health: {healthError}
            <div style={{ marginTop: 8 }}>
              <button type="button" className="loader-retry" onClick={() => setRetryNonce((n) => n + 1)}>Retry</button>
            </div>
          </div>
        ) : !health ? (
          <div className="dash-empty">Scoring your open rooms…</div>
        ) : health.deals.length === 0 ? (
          <div className="dash-empty">
            No open Decision Rooms. Health scores appear as soon as you open a room and buyers start engaging.
          </div>
        ) : (
          <div style={{ marginTop: 14, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--room-border)' }}>
                  <th style={hlTh}>Deal</th>
                  <th style={hlThRight}>Value</th>
                  <th style={hlTh}>Stage</th>
                  <th style={hlThRight}>Health</th>
                  <th style={hlThRight} title="Buyer engagement events, this week vs last">Trend</th>
                  <th style={hlThRight}>Last buyer touch</th>
                  <th style={hlTh}>Attention</th>
                </tr>
              </thead>
              <tbody>
                {health.deals.map((d, i) => (
                  <tr
                    key={d.id}
                    onClick={() => onOpenSpace?.(d.id)}
                    style={{
                      background: i % 2 === 0 ? 'transparent' : 'rgba(var(--on-bg), 0.02)',
                      borderBottom: '1px solid rgba(var(--on-bg), 0.05)',
                      cursor: onOpenSpace ? 'pointer' : 'default',
                      transition: 'background 0.12s ease',
                    }}
                    onMouseEnter={(e) => { if (onOpenSpace) e.currentTarget.style.background = 'rgba(99, 102, 241, 0.07)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(var(--on-bg), 0.02)'; }}
                    title={onOpenSpace ? 'Open this Decision Room' : undefined}
                  >
                    <td style={hlTd}>
                      <div style={{ fontWeight: 600, color: 'var(--room-text-strong)' }}>{d.prospectCompanyName}</div>
                      {health.scope === 'company' && d.ownerName && (
                        <div style={{ fontSize: 11, color: 'var(--room-text-subtle)' }}>{d.ownerName}</div>
                      )}
                    </td>
                    <td style={hlTdRight}>{d.dealValueCents != null ? fmtMoney(d.dealValueCents) : '—'}</td>
                    <td style={hlTd}>
                      <span style={{ color: 'var(--room-text-body)' }}>{d.stageName || '—'}</span>
                      {d.esignStatus && (
                        <span style={{
                          marginLeft: 6, padding: '1px 7px', borderRadius: 100, fontSize: 10, fontWeight: 700,
                          background: d.esignStatus === 'completed' ? 'rgba(16,185,129,0.14)' : 'rgba(99,102,241,0.12)',
                          color: d.esignStatus === 'completed' ? '#047857' : '#4f46e5',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>
                          {d.esignStatus === 'completed' ? 'Signed' : `eSign ${d.esignStatus}`}
                        </span>
                      )}
                    </td>
                    <td style={hlTdRight}>
                      {d.insufficientSignal ? (
                        <span style={{
                          padding: '3px 9px', borderRadius: 100, fontSize: 11, fontWeight: 600,
                          background: 'rgba(var(--on-bg), 0.06)', color: 'var(--room-text-subtle)',
                        }} title="Fewer than 3 engagement events — too new to score honestly">
                          Not enough signal
                        </span>
                      ) : (
                        <span style={{
                          padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 800,
                          background: d.score >= 70 ? 'rgba(16,185,129,0.14)' : d.score >= 40 ? 'rgba(245,158,11,0.14)' : 'rgba(239,68,68,0.12)',
                          color: d.score >= 70 ? '#047857' : d.score >= 40 ? '#b45309' : '#dc2626',
                        }}>
                          {d.score}
                        </span>
                      )}
                    </td>
                    <td style={hlTdRight}>
                      {d.insufficientSignal ? '—' : (
                        <span style={{
                          fontWeight: 700,
                          color: d.events7d > d.eventsPrev7d ? '#047857'
                               : d.events7d < d.eventsPrev7d ? '#dc2626' : 'var(--room-text-subtle)',
                        }}>
                          {d.events7d > d.eventsPrev7d ? '↑' : d.events7d < d.eventsPrev7d ? '↓' : '→'} {d.events7d}
                        </span>
                      )}
                    </td>
                    <td style={hlTdRight}>
                      {d.daysSinceTouch == null ? '—'
                        : d.daysSinceTouch === 0 ? 'Today'
                        : `${d.daysSinceTouch}d ago`}
                    </td>
                    <td style={{ ...hlTd, maxWidth: 220 }}>
                      <span style={{ color: d.riskReason ? '#b45309' : 'var(--room-text-subtle)', fontSize: 12 }}>
                        {d.riskReason || (d.insufficientSignal ? 'Share the room to start the signal' : 'Healthy')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Funnel + forecast, side-by-side on desktop */}
      {/* auto-fit → single column on narrow screens (Tier-2 mobile fix) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        {/* Funnel card */}
        <div className="dash-card">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Deal cycle funnel</h3>
            <span className="dash-card-sub">{periodLabel(data.period)}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
            {stages.map((stage, i) => {
              const widthPct = Math.round((stage.count / maxCount) * 100);
              const prev = i > 0 ? stages[i - 1].count : null;
              const dropoff = prev != null && prev > 0
                ? pct(stage.count, prev)
                : null;
              const clickable = !!onNavigate;
              return (
                <button
                  key={stage.key}
                  type="button"
                  onClick={() => clickable && navigateTo(stage.target)}
                  disabled={!clickable}
                  title={clickable ? `Open ${stage.label}` : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: 'transparent', border: 'none', padding: 0,
                    cursor: clickable ? 'pointer' : 'default',
                    fontFamily: 'inherit', textAlign: 'left',
                    transition: 'opacity 0.12s ease',
                  }}
                  onMouseEnter={(e) => { if (clickable) e.currentTarget.style.opacity = '0.85'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  <div style={{ width: 130, flex: 'none', fontSize: 13, fontWeight: 600, color: 'var(--room-text-body)' }}>
                    {stage.label}
                  </div>
                  <div style={{ flex: 1, position: 'relative', height: 28, background: 'var(--room-card-hover)', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.max(2, widthPct)}%`, height: '100%',
                      background: stage.bar, borderRadius: 8,
                      transition: 'width 0.3s ease',
                    }} />
                    <div style={{
                      position: 'absolute', inset: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      paddingRight: 12, fontSize: 13, fontWeight: 700, color: 'var(--room-text-strong)',
                    }}>
                      {stage.count}
                      {/* Trend vs the previous window (null on 'all'). */}
                      <DeltaChip curr={stage.count} prev={data.previousFunnel ? data.previousFunnel[stage.key] : null} />
                    </div>
                  </div>
                  <div style={{
                    width: 60, flex: 'none', textAlign: 'right',
                    fontSize: 12, color: dropoff == null ? 'transparent' : 'var(--room-text-muted, #64748b)',
                  }}>
                    {dropoff == null ? '—' : `${dropoff}%`}
                  </div>
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--room-text-subtle)' }}>
            % column shows conversion from the stage above. Bigger drop = bigger leak.
          </div>
        </div>

        {/* Forecast card — drills into Decision Rooms phase */}
        <button
          type="button"
          className="dash-card"
          onClick={() => navigateTo('buyer-spaces')}
          disabled={!onNavigate}
          style={{
            textAlign: 'left',
            cursor: onNavigate ? 'pointer' : 'default',
            transition: 'transform 0.12s ease, box-shadow 0.12s ease',
            fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => {
            if (!onNavigate) return;
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div className="dash-card-head">
            <h3 className="dash-card-title">Open pipeline</h3>
            <span className="dash-card-sub">right now</span>
          </div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--room-text-strong)', letterSpacing: '-0.02em' }}>
              {fmtMoney(forecast.totalCents)}
            </div>
            <div style={{ fontSize: 13, color: 'var(--room-text-muted)' }}>
              across {forecast.openRoomCount} open Decision Room{forecast.openRoomCount === 1 ? '' : 's'}
            </div>
            {onNavigate && (
              <div style={{
                marginTop: 8, fontSize: 12, fontWeight: 600,
                color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                Open all rooms →
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Stuck + Hot, side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        <div className="dash-card">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Stuck deals</h3>
            <span className="dash-card-sub">{stuckDeals.length} room{stuckDeals.length === 1 ? '' : 's'}</span>
          </div>
          {stuckDeals.length === 0 ? (
            <div className="dash-empty">Nothing stuck. Every open Decision Room has had activity in the last 7 days.</div>
          ) : (
            <ul className="dash-list">
              {stuckDeals.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => onOpenSpace?.(d.id)}
                    className="dash-list-btn dash-list-btn--warn"
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
                        {d.prospectCompanyName}
                        {/* Value at stake — "$40k quiet for 12 days"
                            motivates a different response than "quiet
                            for 12 days" (2026-07-26 design audit). */}
                        {d.dealValueCents > 0 && (
                          <span style={{ marginLeft: 8, fontWeight: 700, color: '#b45309' }}>
                            {fmtMoney(d.dealValueCents)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginTop: 2 }}>
                        Last activity {d.daysStuck} day{d.daysStuck === 1 ? '' : 's'} ago
                      </div>
                    </div>
                    <span style={{
                      padding: '3px 10px', borderRadius: 100,
                      background: d.daysStuck >= 14 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(245, 158, 11, 0.14)',
                      color: d.daysStuck >= 14 ? '#dc2626' : '#b45309',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                    }}>
                      {d.daysStuck >= 14 ? 'Cold' : 'Quiet'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="dash-card">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Hot this week</h3>
            <span className="dash-card-sub">last 7 days</span>
          </div>
          {hotThisWeek.length === 0 ? (
            <div className="dash-empty">No engagement events in the last 7 days. Nothing&apos;s heating up yet.</div>
          ) : (
            <ul className="dash-list">
              {hotThisWeek.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => onOpenSpace?.(d.id)}
                    className="dash-list-btn"
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
                        {d.prospectCompanyName}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--room-text-muted)', marginTop: 2 }}>
                        {d.stakeholderCount} stakeholder{d.stakeholderCount === 1 ? '' : 's'} ·{' '}
                        {d.viewEventCount} view event{d.viewEventCount === 1 ? '' : 's'}
                        {d.status === 'lost' ? ' · watching again since you marked it lost' : ''}
                      </div>
                    </div>
                    {/* A closed room only reaches this list through
                        post-close buyer activity; the chip says which
                        kind of hot this is. Status chips stay hardcoded
                        pastel/ink PAIRS per the theming rules. */}
                    <span style={{
                      padding: '3px 10px', borderRadius: 100,
                      background: d.status === 'lost' ? 'rgba(220, 38, 38, 0.12)' : 'rgba(16, 185, 129, 0.14)',
                      color: d.status === 'lost' ? '#b91c1c' : '#047857',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                    }}>
                      {d.status === 'lost' ? 'Lost · back' : 'Hot'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
